// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type TradeDetails } from '@shared/data-access/interfaces/redux/trades';
import { type OptionsObject, useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  selectTaxPositions,
  selectTradesTable,
} from '@shared/data-access/slices/portfolio-positions';
import {
  type TradeParams,
  selectLoadingTrades,
  selectTraderTrades,
} from '@advisor-app/store/slices/trader';
import { selectWorkflowAsset } from '@advisor-app/store/slices/workflow';
import { equityApi } from '@advisor-app/store/api/brokerage/equity-api';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { useTranslation } from 'react-i18next';
import {
  OperationType,
  type BuySellTransferOperation,
} from '@shared/data-access/interfaces/operation-type';
import { arrayToMap } from '@advisor-app/features/utils/array-to-map';
import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';
import { useAdvisorRole } from '@advisor-app/hooks/use-advisor-role';
import {
  buildToAssetDetails,
  buildTradeColumnName,
  fetchAssetInfoMap,
} from '../utils/trade-asset-metadata';
import { resolveTradeInput } from '../utils/resolve-trade-input';
import { selectClientProductsAccounts } from '@shared/data-access/slices/clients/clients-selectors';
import {
  AccountStatus,
  type AdvisoryInstitution,
  type InstitutionAccount,
} from '@shared/data-access/types/brokerage';
import { isNuvaraInversisInstitution } from '@shared/data-access/utils/nuvara-inversis-institution';

export type TradeInputParameters = Pick<
  TradeDetails,
  | 'ticker'
  | 'operation'
  | 'volume'
  | 'sourceMyInvestments'
  | 'institution_id'
  | 'institution_name'
  | 'account_id'
  | 'from_iban'
  | 'to_iban'
  | 'inputType'
  | 'name'
> & {
  tickerInfo?: any;
  asset_type?: string;
  unrealized_capital_gains?: number;
  transfer_target?: string;
  contracts_number?: number;
};

const commonSnackProps: OptionsObject = {
  variant: 'success',
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
};

type ClientInstitutionAccounts = AdvisoryInstitution & {
  accounts: InstitutionAccount[];
};

const pickActiveAccount = (
  institution: ClientInstitutionAccounts | undefined,
  type: 'checking' | 'investment',
) => {
  if (!institution) {
    return undefined;
  }

  const matches = institution.accounts.filter(
    account =>
      account.type === type && account.account_status === AccountStatus.Active,
  );

  if (matches.length === 0) {
    return undefined;
  }

  return [...matches].sort((left, right) => {
    const cashLeft = left.available ?? left.cash ?? 0;
    const cashRight = right.available ?? right.cash ?? 0;
    if (cashRight !== cashLeft) {
      return cashRight - cashLeft;
    }

    return left.iban.localeCompare(right.iban);
  })[0];
};

const institutionCash = (institution: ClientInstitutionAccounts) =>
  institution.accounts.reduce(
    (sum, account) => sum + (account.available ?? account.cash ?? 0),
    0,
  );

const findInstitutionByIban = (
  institutions: ClientInstitutionAccounts[],
  iban?: string,
) =>
  iban
    ? institutions.find(institution =>
        institution.accounts.some(account => account.iban === iban),
      )
    : undefined;

const resolveDefaultAccountIbans = (
  operation: TradeDetails['operation'],
  holdingIban: string | undefined,
  holdingInstitutionId: string | undefined,
  institutions: ClientInstitutionAccounts[],
) => {
  const inversis = institutions.find(institution =>
    isNuvaraInversisInstitution(institution),
  );
  const holdingInstitution =
    institutions.find(institution => institution.id === holdingInstitutionId) ??
    findInstitutionByIban(institutions, holdingIban);
  const fallbackInstitution = [...institutions].sort((left, right) => {
    const cashDiff = institutionCash(right) - institutionCash(left);
    if (cashDiff !== 0) {
      return cashDiff;
    }

    return left.id.localeCompare(right.id);
  })[0];

  const buyFromInstitution = inversis ?? fallbackInstitution;
  const buyFrom = pickActiveAccount(buyFromInstitution, 'checking');
  const buyToInstitution = inversis ?? buyFromInstitution;
  const buyTo = pickActiveAccount(buyToInstitution, 'investment');
  const isInversisHolding =
    isNuvaraInversisInstitution(holdingInstitution) ||
    isNuvaraInversisInstitution(holdingInstitutionId);

  if (operation === OperationType.BUY) {
    return {
      from_iban: buyFrom?.iban,
      to_iban: buyTo?.iban,
      institution_id: buyFromInstitution?.id ?? holdingInstitutionId,
      institution_name: buyFromInstitution?.name,
    };
  }

  if (operation === OperationType.SELL && isInversisHolding) {
    const holdingAccount = holdingIban
      ? (inversis ?? holdingInstitution)?.accounts.find(
          account => account.iban === holdingIban,
        )
      : undefined;
    const fromAccount =
      holdingAccount?.type === 'investment'
        ? holdingAccount
        : pickActiveAccount(inversis ?? holdingInstitution, 'investment');
    const toAccount = pickActiveAccount(inversis ?? holdingInstitution, 'checking');

    return {
      from_iban: fromAccount?.iban ?? holdingIban,
      to_iban: toAccount?.iban,
      institution_id: (inversis ?? holdingInstitution)?.id ?? holdingInstitutionId,
      institution_name: (inversis ?? holdingInstitution)?.name,
    };
  }

  if (operation === OperationType.SELL) {
    return {
      from_iban: holdingIban,
      to_iban: holdingIban,
      institution_id: holdingInstitutionId,
      institution_name: holdingInstitution?.name,
    };
  }

  if (operation === OperationType.TRANSFER) {
    return {
      from_iban: holdingIban,
      to_iban: buyTo?.iban,
      institution_id: holdingInstitutionId ?? buyToInstitution?.id,
      institution_name: holdingInstitution?.name ?? buyToInstitution?.name,
    };
  }

  return {};
};

export const useSendToTrader = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { canUseTrader } = useAdvisorRole();

  const trades = useAppSelector(selectTradesTable);

  const traderPositions = useAppSelector(selectTraderTrades);
  const clientProductAccounts = useAppSelector(selectClientProductsAccounts);

  const [getOrderPrices] = equityApi.useLazyGetOrderPricesQuery();

  const taxHarvestingPositions = useAppSelector(selectTaxPositions);

  const assetType = useAppSelector(selectWorkflowAsset);

  const isLoadingTrades = useAppSelector(selectLoadingTrades);

  const findCurrentTrade = useCallback(
    (ticker: string, operation: string, transfer_target?: string) => {
      return (traderPositions.find(
        position =>
          position.ticker === ticker &&
          position.operation === operation &&
          position.transfer_target === transfer_target,
      ) ?? {
        ticker: ticker,
        operation: operation,
        volume: 0,
        realized_capital_gains: 0,
        unrealized_capital_gains: 0,
        contracts_number: 0,
        transfer_target: transfer_target,
        inputType: undefined,
      }) as TradeDetails;
    },
    [traderPositions],
  );

  const mergeEqualTrades = useCallback(
    (...trades: [TradeParams, TradeParams]) =>
      trades.reduce((prev, current) => ({
        ...prev,
        volume: prev?.volume + current.volume,
        realized_capital_gains: current.realized_capital_gains,
        unrealized_capital_gains:
          current.unrealized_capital_gains + (prev.unrealized_capital_gains ?? 0),
        contracts_number:
          (current.contracts_number ?? 0) + (prev.contracts_number ?? 0),
        inputType: current.inputType ?? prev?.inputType,
      })),
    [],
  );

  const mergeTradesWithAccounts = useCallback(
    (params: TradeParams, current: TradeDetails) => {
      const defaults = resolveDefaultAccountIbans(
        params.operation,
        params.account_id ?? current.account_id,
        params.institution_id ?? current.institution_id,
        clientProductAccounts,
      );
      const from_iban = current.from_iban ?? params.from_iban ?? defaults.from_iban;
      const to_iban = current.to_iban ?? params.to_iban ?? defaults.to_iban;
      const institution_id = current.from_iban
        ? current.institution_id
        : params.from_iban
        ? params.institution_id
        : defaults.institution_id ??
          params.institution_id ??
          current.institution_id;
      const institution_name = current.from_iban
        ? current.institution_name
        : params.from_iban
        ? params.institution_name
        : defaults.institution_name ??
          params.institution_name ??
          current.institution_name;

      return mergeEqualTrades(
        {
          ...params,
          from_iban,
          to_iban,
          institution_id,
          institution_name,
        },
        current,
      );
    },
    [clientProductAccounts, mergeEqualTrades],
  );

  const sendTradesTickers = useCallback(
    async (...trades: TradeInputParameters[]) => {
      if (!canUseTrader) return;

      const uniqueTickers = Array.from(
        new Set(
          trades
            .flatMap(trade => [trade.ticker, trade.transfer_target])
            .filter((ticker): ticker is string => Boolean(ticker)),
        ),
      );

      const assetInfo = await getAssetsInfo(uniqueTickers, dispatch);

      const assetInfoMap = arrayToMap(assetInfo, 'ticker');

      // Convert TradeInputParameters to TradeParams
      const tradeParams: TradeParams[] = trades.map(trade => {
        const info = assetInfoMap.get(trade.ticker);

        const transferTargetInfo = trade.transfer_target
          ? assetInfoMap.get(trade.transfer_target)
          : undefined;

        const currentTrade = findCurrentTrade(
          trade.ticker,
          trade.operation,
          trade.transfer_target,
        );

        const columnName = buildTradeColumnName(info, {
          asset_type: trade.asset_type,
          name: trade.name ?? undefined,
          url_logo: trade.tickerInfo?.url_logo,
        });

        const { inputType, contracts_number } = resolveTradeInput({
          inputType: trade.inputType,
          contracts_number: trade.contracts_number ?? trade.tickerInfo?.quantity,
          assetType: columnName.asset_type,
        });

        return mergeTradesWithAccounts(
          {
            operation: trade.operation,
            ticker: trade.ticker,
            isin: info?.isin,
            volume: trade.volume,
            realized_capital_gains: 0,
            unrealized_capital_gains: trade.unrealized_capital_gains ?? 0,
            institution_id: trade.institution_id,
            institution_name: trade.institution_name,
            account_id: trade.account_id,
            from_iban: trade.from_iban,
            to_iban: trade.to_iban,
            columnName,
            transfer_target: trade.transfer_target,
            ...(trade.operation === OperationType.TRANSFER && {
              to_isin: transferTargetInfo?.isin ?? undefined,
              to_asset_details: buildToAssetDetails(
                transferTargetInfo,
                trade.transfer_target,
              ),
            }),
            inputType,
            contracts_number,
          },
          currentTrade,
        );
      });

      return getOrderPrices(tradeParams);
    },
    [
      canUseTrader,
      findCurrentTrade,
      getOrderPrices,
      mergeTradesWithAccounts,
      dispatch,
    ],
  );

  /* istanbul ignore next */
  const sendTradeTicker = useCallback(
    async (trade: TradeInputParameters) => {
      if (!canUseTrader) return;

      enqueueSnackbar(
        t('common.ticker-added', {
          ns: 'common',
          ticker: trade.ticker,
        }) as string,
        {
          ...commonSnackProps,
        },
      );

      const currentTrade = findCurrentTrade(
        trade.ticker,
        trade.operation,
        trade.transfer_target,
      );

      if (trade.sourceMyInvestments) {
        const assetInfo = await getAssetsInfo([trade.ticker], dispatch);
        const info = assetInfo[0];
        const columnName = buildTradeColumnName(info, {
          url_logo: trade?.tickerInfo?.url_logo,
          name: trade.name ?? trade.tickerInfo?.name,
          asset_type: trade.asset_type,
          dfi_url: trade?.tickerInfo?.dfi_url,
          ticker: trade.ticker,
        });
        const { inputType, contracts_number } = resolveTradeInput({
          inputType: trade.inputType,
          contracts_number: trade.contracts_number ?? trade.tickerInfo?.quantity,
          assetType: columnName.asset_type,
        });

        return getOrderPrices([
          mergeTradesWithAccounts(
            {
              ticker: trade?.ticker,
              operation: trade?.operation,
              volume: trade?.volume,
              realized_capital_gains: 0,
              unrealized_capital_gains: trade.unrealized_capital_gains ?? 0,
              isin: info?.isin,
              columnName,
              transfer_target: trade.transfer_target,
              inputType,
              contracts_number,
              institution_id: trade.institution_id,
              institution_name: trade.institution_name,
              account_id: trade.account_id,
              from_iban: trade.from_iban,
              to_iban: trade.to_iban,
            },
            currentTrade,
          ),
        ]);
      }

      const assetInfo = await getAssetsInfo([trade.ticker], dispatch);
      const isError = assetInfo.length === 0;

      if (isError) {
        return;
      }

      const info = assetInfo[0];
      const columnName = buildTradeColumnName(info, {
        asset_type: trade.asset_type,
        name: trade.name ?? trade.tickerInfo?.name,
        url_logo: trade?.tickerInfo?.url_logo,
        ticker: trade.ticker,
      });
      const { inputType, contracts_number } = resolveTradeInput({
        inputType: trade.inputType,
        contracts_number: trade.contracts_number ?? trade.tickerInfo?.quantity,
        assetType: columnName.asset_type,
      });

      return getOrderPrices([
        mergeTradesWithAccounts(
          {
            operation: trade.operation,
            ticker: trade.ticker,
            isin: info?.isin,
            volume: trade?.volume,
            realized_capital_gains: 0,
            unrealized_capital_gains: trade.unrealized_capital_gains ?? 0,
            institution_id: trade?.institution_id,
            institution_name: trade?.institution_name,
            account_id: trade?.account_id,
            from_iban: trade.from_iban,
            to_iban: trade.to_iban,
            columnName,
            transfer_target: trade.transfer_target,
            inputType,
            contracts_number,
          },
          currentTrade,
        ),
      ]);
    },
    [
      canUseTrader,
      enqueueSnackbar,
      findCurrentTrade,
      getOrderPrices,
      mergeTradesWithAccounts,
      t,
      dispatch,
    ],
  );

  const sendRebalanceTickers = useCallback(async () => {
    if (!canUseTrader) return;

    // Extract all unique tickers including from_ticker and to_ticker for TRANSFER operations
    const uniqueTickers = Array.from(
      new Set(
        trades
          .flatMap(trade => {
            const tickers = [trade.ticker];
            if (trade.from_ticker) tickers.push(trade.from_ticker);
            if (trade.to_ticker) tickers.push(trade.to_ticker);
            return tickers;
          })
          .filter(Boolean),
      ),
    );

    // Get asset info for all unique tickers
    const assetInfo = await getAssetsInfo(uniqueTickers, dispatch);
    const assetInfoMap = arrayToMap(assetInfo, 'ticker');

    return getOrderPrices(
      trades.map(trade => {
        const isTransfer = trade.operation === OperationType.TRANSFER;
        const mainTicker = isTransfer
          ? trade.from_ticker ?? trade.ticker
          : trade.ticker;
        const transferTarget = isTransfer
          ? trade.to_ticker ?? trade.transfer_target
          : trade.transfer_target;

        const info = assetInfoMap.get(mainTicker);
        const toInfo =
          isTransfer && transferTarget
            ? assetInfoMap.get(transferTarget)
            : undefined;

        const currentTrade = findCurrentTrade(
          mainTicker,
          trade.operation,
          transferTarget,
        );

        const columnName = buildTradeColumnName(info, {
          url_logo: isTransfer
            ? (info as Stock | undefined)?.url_logo
            : trade.url_logo ?? (info as Stock | undefined)?.url_logo,
          url_morningstar: isTransfer
            ? (info as Stock | undefined)?.url_morningstar
            : trade.url_morningstar ?? (info as Stock | undefined)?.url_morningstar,
          name: trade.name ?? undefined,
          asset_type: trade.asset_type,
          dfi_url: trade.dfi_url,
          ticker: mainTicker,
        });

        const { inputType, contracts_number } = resolveTradeInput({
          inputType: trade.inputType,
          contracts_number: trade.contracts_number,
          assetType: columnName.asset_type,
        });

        return mergeTradesWithAccounts(
          {
            ticker: mainTicker,
            operation: trade.operation,
            volume: trade.volume,
            isin: info?.isin ?? trade.isin,
            realized_capital_gains: trade.realized_capital_gains,
            unrealized_capital_gains: trade.unrealized_capital_gains,
            inputType,
            contracts_number,
            columnName,
            gain_loss: [0, 0],
            local_currency_values: {
              currency: trade.currency ?? '',
              volume: trade.volume,
              expected_volume: trade.volume,
              delta_volume: 0,
            },
            exchange: trade.exchange,
            transfer_target: transferTarget,
            institution_id: trade.institution_id,
            institution_name: trade.institution_name,
            account_id: trade.account_id,
            ...(isTransfer && {
              to_isin: toInfo?.isin ?? trade.to_isin,
              to_asset_details: buildToAssetDetails(toInfo, transferTarget),
            }),
          } as TradeParams,
          currentTrade,
        );
      }),
    );
  }, [
    canUseTrader,
    findCurrentTrade,
    getOrderPrices,
    mergeTradesWithAccounts,
    trades,
    dispatch,
  ]);

  const sendPortfolioPositions = useCallback(async () => {
    if (!canUseTrader) return;

    const assetInfoMap = await fetchAssetInfoMap(
      trades.map(({ ticker }) => ticker),
      dispatch,
    );

    return getOrderPrices(
      trades.map(
        ({
          exchange,
          ticker,
          url_logo,
          url_morningstar,
          asset_type,
          unrealized_capital_gains,
          isin,
          outstanding_balance,
          name,
          operation = OperationType.BUY,
          inputType,
          contracts_number,
          dfi_url,
        }) => {
          const info = assetInfoMap.get(ticker);
          const columnName = buildTradeColumnName(info, {
            url_logo,
            url_morningstar,
            asset_type: asset_type ?? assetType,
            dfi_url,
            name,
            ticker,
          });
          const resolvedInput = resolveTradeInput({
            inputType,
            contracts_number,
            assetType: columnName.asset_type,
          });

          return mergeTradesWithAccounts(
            {
              operation: operation as BuySellTransferOperation,
              realized_capital_gains: 0,
              ticker,
              unrealized_capital_gains,
              volume: outstanding_balance,
              columnName,
              exchange,
              isin: info?.isin ?? isin,
              ...resolvedInput,
            },
            findCurrentTrade(ticker, operation),
          );
        },
      ),
    );
  }, [
    canUseTrader,
    assetType,
    findCurrentTrade,
    getOrderPrices,
    mergeTradesWithAccounts,
    trades,
    dispatch,
  ]);
  /* istanbul ignore next */
  const sendTaxPositions = useCallback(async () => {
    if (!canUseTrader) return;

    const replacementTickers = taxHarvestingPositions.flatMap(({ harvesting }) =>
      harvesting?.replacement?.ticker ? [harvesting.replacement.ticker] : [],
    );
    const assetInfoMap = await fetchAssetInfoMap(
      [
        ...trades.map(({ ticker }) => ticker),
        ...taxHarvestingPositions.map(({ ticker }) => ticker),
        ...replacementTickers,
      ],
      dispatch,
    );

    return getOrderPrices(
      taxHarvestingPositions
        .map(({ price, qty, ticker, unrealized_capital_gains = 0, harvesting }) => {
          const assetInfo = trades.find(asset => asset.ticker === ticker);
          const positionInfo = assetInfoMap.get(ticker);
          const sellColumnName = buildTradeColumnName(positionInfo, {
            url_logo: assetInfo?.url_logo,
            url_morningstar: assetInfo?.url_morningstar,
            asset_type: assetType,
            name: assetInfo?.name ?? positionInfo?.name,
            ticker,
          });
          const volumeTaxHarvesting = price * qty * (harvesting?.percentage || 1);
          const sellInput = resolveTradeInput({
            inputType: 'shares',
            contracts_number: qty * (harvesting?.percentage || 1),
            assetType: sellColumnName.asset_type,
          });

          const trade = mergeTradesWithAccounts(
            {
              ticker: assetInfo?.ticker ?? ticker,
              isin: positionInfo?.isin ?? assetInfo?.isin,
              operation: OperationType.SELL as BuySellTransferOperation,
              volume: volumeTaxHarvesting,
              realized_capital_gains: 0,
              unrealized_capital_gains,
              exchange: assetInfo?.exchange || '-',
              columnName: sellColumnName,
              ...sellInput,
            },
            findCurrentTrade(ticker, OperationType.SELL),
          );

          if (harvesting?.replacement) {
            const replacement = harvesting.replacement;
            const replacementInfo = assetInfoMap.get(replacement.ticker);
            const buyColumnName = buildTradeColumnName(replacementInfo, {
              url_logo: replacement.url_logo,
              name: replacement.name ?? replacementInfo?.name,
              ticker: replacement.ticker,
            });

            return [
              trade,
              {
                ...trade,
                operation: OperationType.BUY as BuySellTransferOperation,
                volume: volumeTaxHarvesting,
                ticker: replacement.ticker,
                isin: replacementInfo?.isin ?? replacement.isin,
                columnName: buyColumnName,
                ...resolveTradeInput({
                  inputType: 'amount',
                  assetType: buyColumnName.asset_type,
                }),
              } satisfies TradeParams,
            ];
          }

          return trade;
        })
        .flat(),
    );
  }, [
    canUseTrader,
    assetType,
    findCurrentTrade,
    getOrderPrices,
    mergeTradesWithAccounts,
    taxHarvestingPositions,
    trades,
    dispatch,
  ]);

  return {
    sendTradeTicker,
    sendTradesTickers,
    sendRebalanceTickers,
    sendPortfolioPositions,
    sendTaxPositions,
    isLoadingTrades,
    mergeEqualTrades,
    findCurrentTrade,
    canSendOperations: canUseTrader,
  };
};
